import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  ViewEncapsulation,
} from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { AccordionModule } from 'primeng/accordion';
import { SidebarModule } from 'primeng/sidebar';
import { distinctUntilChanged, Subject, takeUntil } from 'rxjs';
import { constants } from '../../constants';
import { NumericInputDirective } from '../../directives/numeric-input.directive';
import { RealEstateDeveloper } from '../../models/real-estate-developer.model';
import { RealEstateProjectAmenity, realEstateProjectAmenityLabels, realEstateProjectAmenityValues, RealEstateProjectPhase, realEstateProjectPhaseLabels, realEstateProjectPhaseValues, RealEstateProjectType, realEstateProjectTypeLabels, realEstateProjectTypeValues } from '../../models/real-estate-project.model';
import { CurrencyFormatterPipe } from '../../pipes/currency-formatter.pipe';
import { ErrorReportingService } from '../../services/error-reporting/error-reporting.service';
import { RealEstateDevelopersService } from '../../services/real-estate-developers/real-estate-developers.service';
import { SearchFilter } from '../../services/search/search.service';
import { areEqualObjects } from '../../utils/object.utils';
import { ViewState } from '../../utils/view-state.utils';
import { ButtonComponent } from '../button/button.component';
import { RetryableSectionComponent } from '../retryable-section/retryable-section.component';
import { RealEstateLocationsService } from '../../services/real-estate-locations/real-estate-locations.service';

interface RealEstateFilterFormValues {
  amenities: RealEstateProjectAmenity[] | null;
  areaMax: number | null;
  areaMin: number | null;
  bathroomsMax: number | null;
  bathroomsMin: number | null;
  bedroomsMax: number | null;
  bedroomsMin: number | null;
  developerIds: number[] | null;
  municipality: string | null;
  phases: RealEstateProjectPhase[] | null;
  priceMax: number | null;
  priceMin: number | null;
  settlements: string[] | null;
  sortBy: SortValue | null;
  state: string | null;
  types: RealEstateProjectType[] | null;
}

export interface RealEstateFilterSidebarValues {
  filters: SearchFilter[] | null;
  order: SortValue | null;
}

export interface SortValue {
  direction: 'asc' | 'desc';
  field: string;
}

@Component({
  selector: 'app-real-estate-filter-sidebar',
  standalone: true,
  imports: [
    AccordionModule,
    ButtonComponent,
    CommonModule,
    CurrencyFormatterPipe,
    FormsModule,
    NumericInputDirective,
    ReactiveFormsModule,
    SidebarModule,
    RetryableSectionComponent,
  ],
  encapsulation: ViewEncapsulation.None,
  templateUrl: './real-estate-filter-sidebar.component.html',
  styleUrl: './real-estate-filter-sidebar.component.scss',
})
export class RealEstateFilterSidebarComponent implements OnChanges, OnDestroy {
  @Input() currency = constants.defaultCurrency;
  @Input() locale = constants.defaultLocale;
  @Input() values: RealEstateFilterSidebarValues = { filters: null, order: null };
  @Output() changeFilters = new EventEmitter<RealEstateFilterSidebarValues>();
  @Output() hasActiveFilters = new EventEmitter<boolean>();
  protected developersMap: Map<number, RealEstateDeveloper> = new Map();
  protected developersViewState: ViewState<RealEstateDeveloper[]> = { status: 'initial' };
  protected form: FormGroup<{
    amenities: FormControl<RealEstateProjectAmenity[] | null>;
    areaMax: FormControl<number | null>;
    areaMin: FormControl<number | null>;
    bathroomsMax: FormControl<number | null>;
    bathroomsMin: FormControl<number | null>;
    bedroomsMax: FormControl<number | null>;
    bedroomsMin: FormControl<number | null>;
    developerIds: FormControl<number[] | null>;
    municipality: FormControl<string | null>;
    phases: FormControl<RealEstateProjectPhase[] | null>;
    priceMax: FormControl<number | null>;
    priceMin: FormControl<number | null>;
    settlements: FormControl<string[] | null>;
    sortBy: FormControl<SortValue | null>;
    state: FormControl<string | null>;
    types: FormControl<RealEstateProjectType[] | null>;
  }>;
  protected isMobile = false;
  protected isVisible = false;
  protected municipalitiesViewState: ViewState<string[]> = { status: 'initial' };
  protected settlementsViewState: ViewState<string[]> = { status: 'initial' };
  protected amenityLabels = realEstateProjectAmenityLabels;
  protected amenityOptions = realEstateProjectAmenityValues.map((value) => ({ label: realEstateProjectAmenityLabels[value], value }));
  protected phaseLabels = realEstateProjectPhaseLabels;
  protected phaseOptions = realEstateProjectPhaseValues.map((value) => ({ label: realEstateProjectPhaseLabels[value], value }));
  protected sortOptionDefault: SortValue;
  protected sortOptions: { label: string; value: SortValue; }[] = [
    { label: 'Mejor coincidencia', value: { field: 'default', direction: 'desc' } },
    { label: 'Precio de mayor a menor', value: { field: 'price', direction: 'desc' } },
    { label: 'Precio de menor a mayor', value: { field: 'price', direction: 'asc' } },
  ];
  protected stateOptionDefault = '';
  protected statesViewState: ViewState<string[]> = { status: 'initial' };
  protected typeLabels = realEstateProjectTypeLabels;
  protected typeOptions = realEstateProjectTypeValues.map((value) => ({ label: realEstateProjectTypeLabels[value], value }));
  private viewDestroyed = new Subject<void>();

  constructor(
    private developersService: RealEstateDevelopersService,
    private errorReportingService: ErrorReportingService,
    private realEstateLocationsService: RealEstateLocationsService,
  ) {
    this.sortOptionDefault = this.sortOptions[0].value;
    this.form = new FormGroup({
      amenities: new FormControl<RealEstateProjectAmenity[] | null>(null),
      areaMax: new FormControl<number | null>(null),
      areaMin: new FormControl<number | null>(null),
      bathroomsMax: new FormControl<number | null>(null),
      bathroomsMin: new FormControl<number | null>(null),
      bedroomsMax: new FormControl<number | null>(null),
      bedroomsMin: new FormControl<number | null>(null),
      developerIds: new FormControl<number[] | null>(null),
      municipality: new FormControl<string | null>(null),
      phases: new FormControl<RealEstateProjectPhase[] | null>(null),
      priceMax: new FormControl<number | null>(null),
      priceMin: new FormControl<number | null>(null),
      settlements: new FormControl<string[] | null>(null),
      sortBy: new FormControl<SortValue | null>(this.sortOptionDefault),
      state: new FormControl<string | null>(this.stateOptionDefault),
      types: new FormControl<RealEstateProjectType[] | null>(null),
    });
    this.form.controls.state.valueChanges.pipe(distinctUntilChanged(), takeUntil(this.viewDestroyed)).subscribe((state) => {
      this.form.controls.municipality.setValue(null);
      if (state && state !== this.stateOptionDefault) {
        this.loadMunicipalities();
      } else {
        this.municipalitiesViewState = { status: 'initial' };
      }
    });
    this.form.controls.municipality.valueChanges.pipe(distinctUntilChanged(), takeUntil(this.viewDestroyed)).subscribe((municipality) => {
      this.form.controls.settlements.setValue([]);
      if (municipality && this.form.controls.state.value) {
        this.loadSettlements();
      } else {
        this.settlementsViewState = { status: 'initial' };
      }
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['values']) {
      this.applyValuesToForm();
    }
  }

  ngOnDestroy(): void {
    this.viewDestroyed.next();
  }

  // eslint-disable-next-line @typescript-eslint/member-ordering
  @HostListener('window:resize')
  onResize(): void {
    this.isMobile = window.innerWidth <= 480;
  }

  close(): void {
    this.isVisible = false;
  }

  clearFilters(): void {
    this.form.patchValue({
      amenities: [],
      areaMax: null,
      areaMin: null,
      bathroomsMax: null,
      bathroomsMin: null,
      bedroomsMax: null,
      bedroomsMin: null,
      developerIds: [],
      municipality: null,
      phases: [],
      priceMax: null,
      priceMin: null,
      settlements: [],
      sortBy: this.sortOptionDefault,
      state: this.stateOptionDefault,
      types: [],
    }, { emitEvent: false });
    this.form.updateValueAndValidity();
  }

  open(): void {
    this.applyValuesToForm();
    this.isVisible = true;
    this.loadDevelopers();
    this.loadStates();
    if (this.form.controls.state.value) {
      this.loadMunicipalities();
    }
    if (this.form.controls.municipality.value) {
      this.loadSettlements();
    }
  }

  protected cancel(): void {
    this.close();
  }

  protected filter(): void {
    this.changeFilters.emit(this.getFilterSidebarValues());
    this.hasActiveFilters.emit(this.hasActiveFiltersInForm);
    this.close();
  }

  protected deleteValue<T>(control: FormControl<T[] | null>, value: T): void {
    const values = control.value;
    const valuesFiltered = values ? values.filter((v) => v !== value) : [];
    control.patchValue(valuesFiltered);
  }

  protected getSortByLabel(sortValue: SortValue | null): string {
    return this.sortOptions.find(({ value }) => value.field === sortValue?.field && value.direction === sortValue?.direction)?.label ?? '';
  }

  protected get hasActiveFiltersInForm(): boolean {
    const { filters, order } = this.getFilterSidebarValues();
    return filters !== null || order !== null;
  }

  protected async loadDevelopers(): Promise<void> {
    this.developersViewState = { status: 'loading' };
    try {
      const developers = await this.developersService.getAll({ limit: 100, page: 1 });
      this.developersMap.clear();
      developers.data.forEach((developer) => this.developersMap.set(developer.id, developer));
      this.developersViewState = { status: 'success', value: developers.data };
    } catch (error) {
      this.errorReportingService.log('RealEstateFilterSidebarComponent.loadDevelopers()', 'get-real-estate-developers', error);
      this.developersViewState = { status: 'error' };
    }
  }

  protected async loadMunicipalities(): Promise<void> {
    const state = this.form.controls.state.value;
    if (!state) {
      return;
    }
    this.municipalitiesViewState = { status: 'loading' };
    try {
      const municipalities = await this.realEstateLocationsService.getAllMunicipalities({
        state,
        limit: 100,
        page: 1,
      });
      if (this.form.controls.state.value !== state) {
        return;
      }
      this.municipalitiesViewState = { status: 'success', value: municipalities.data };
    } catch (error) {
      this.errorReportingService.log('RealEstateFilterSidebarComponent.loadMunicipalities()', 'get-municipalities', error);
      if (this.form.controls.state.value === state) {
        this.municipalitiesViewState = { status: 'error' };
      }
    }
  }

  protected async loadSettlements(): Promise<void> {
    const municipality = this.form.controls.municipality.value;
    const state = this.form.controls.state.value;
    if (!municipality || !state) {
      return;
    }
    this.settlementsViewState = { status: 'loading' };
    try {
      const settlements = await this.realEstateLocationsService.getAllNeighborhoods({
        municipality,
        state,
        limit: 500,
        page: 1,
      });
      if (this.form.controls.municipality.value !== municipality || this.form.controls.state.value !== state) {
        return;
      }
      this.settlementsViewState = { status: 'success', value: settlements.data };
    } catch (error) {
      this.errorReportingService.log('RealEstateFilterSidebarComponent.loadSettlements()', 'get-neighborhoods', error);
      if (this.form.controls.municipality.value === municipality && this.form.controls.state.value === state) {
        this.settlementsViewState = { status: 'error' };
      }
    }
  }

  protected async loadStates(): Promise<void> {
    this.statesViewState = { status: 'loading' };
    try {
      const states = await this.realEstateLocationsService.getAllStates({ limit: 100, page: 1 });
      this.statesViewState = { status: 'success', value: states.data };
    } catch (error) {
      this.errorReportingService.log('RealEstateFilterSidebarComponent.loadStates()', 'get-states', error);
      this.statesViewState = { status: 'error' };
    }
  }

  protected onArrayValueToggle<T>(event: Event, control: FormControl<T[] | null>, value: T): void {
    const values = control.value ?? [];
    const checked = (event.target as HTMLInputElement).checked;
    control.setValue(checked ? [...values, value] : values.filter((currentValue) => currentValue !== value));
    control.markAsDirty();
    control.markAsTouched();
  }

  protected resetControls<T>(controls: FormControl | FormControl[], value?: T): void {
    if (Array.isArray(controls)) {
      controls.forEach((control) => control.patchValue(value ?? null));
    } else {
      controls.patchValue(value ?? null);
    }
  }

  private applyValuesToForm(): void {
    const { amenities, areaMax, areaMin, bathroomsMax, bathroomsMin, bedroomsMax, bedroomsMin, developerIds, municipality, phases, priceMax, priceMin, settlements, sortBy, state, types } = this.getFormValues();
    this.form.patchValue({
      amenities,
      areaMax,
      areaMin,
      bathroomsMax,
      bathroomsMin,
      bedroomsMax,
      bedroomsMin,
      developerIds,
      municipality,
      phases,
      priceMax,
      priceMin,
      settlements,
      sortBy: this.sortOptions.find((option) => areEqualObjects(option.value, sortBy))?.value ?? this.sortOptionDefault,
      state,
      types,
    }, { emitEvent: false });
    this.form.updateValueAndValidity();
    this.hasActiveFilters.emit(this.hasActiveFiltersInForm);
  }

  private getFormValues(): RealEstateFilterFormValues {
    const { filters, order } = this.values;
    return {
      amenities: filters?.find((f) => f.field === 'amenities')?.value as RealEstateProjectAmenity[] ?? [],
      areaMax: filters?.find((f) => f.field === 'area' && f.operator === '<=')?.value as number ?? null,
      areaMin: filters?.find((f) => f.field === 'area' && f.operator === '>=')?.value as number ?? null,
      bathroomsMax: filters?.find((f) => f.field === 'bathrooms' && f.operator === '<=')?.value as number ?? null,
      bathroomsMin: filters?.find((f) => f.field === 'bathrooms' && f.operator === '>=')?.value as number ?? null,
      bedroomsMax: filters?.find((f) => f.field === 'bedrooms' && f.operator === '<=')?.value as number ?? null,
      bedroomsMin: filters?.find((f) => f.field === 'bedrooms' && f.operator === '>=')?.value as number ?? null,
      developerIds: filters?.find((f) => f.field === 'developerIds')?.value as number[] ?? [],
      municipality: filters?.find((f) => f.field === 'municipality')?.value as string ?? null,
      phases: filters?.find((f) => f.field === 'phases')?.value as RealEstateProjectPhase[] ?? [],
      priceMax: filters?.find((f) => f.field === 'price' && f.operator === '<=')?.value as number ?? null,
      priceMin: filters?.find((f) => f.field === 'price' && f.operator === '>=')?.value as number ?? null,
      settlements: filters?.find((f) => f.field === 'settlements')?.value as string[] ?? [],
      sortBy: order,
      state: filters?.find((f) => f.field === 'state')?.value as string ?? this.stateOptionDefault,
      types: filters?.find((f) => f.field === 'types')?.value as RealEstateProjectType[] ?? [],
    };
  }

  private getFilterSidebarValues(): RealEstateFilterSidebarValues {
    const { amenities, areaMax, areaMin, bathroomsMax, bathroomsMin, bedroomsMax, bedroomsMin, developerIds, municipality, phases, priceMax, priceMin, settlements, sortBy, state, types } = this.form.getRawValue();
    const searchFilters: SearchFilter[] = [];
    if (amenities?.length) {
      searchFilters.push({ field: 'amenities', operator: 'in', value: amenities });
    }
    if (areaMax) {
      searchFilters.push({ field: 'area', operator: '<=', value: areaMax });
    }
    if (areaMin) {
      searchFilters.push({ field: 'area', operator: '>=', value: areaMin });
    }
    if (bathroomsMax) {
      searchFilters.push({ field: 'bathrooms', operator: '<=', value: bathroomsMax });
    }
    if (bathroomsMin) {
      searchFilters.push({ field: 'bathrooms', operator: '>=', value: bathroomsMin });
    }
    if (bedroomsMax) {
      searchFilters.push({ field: 'bedrooms', operator: '<=', value: bedroomsMax });
    }
    if (bedroomsMin) {
      searchFilters.push({ field: 'bedrooms', operator: '>=', value: bedroomsMin });
    }
    if (developerIds?.length) {
      searchFilters.push({ field: 'developerIds', operator: 'in', value: developerIds });
    }
    if (municipality) {
      searchFilters.push({ field: 'municipality', operator: '=', value: municipality });
    }
    if (phases?.length) {
      searchFilters.push({ field: 'phases', operator: 'in', value: phases });
    }
    if (priceMax) {
      searchFilters.push({ field: 'price', operator: '<=', value: priceMax });
    }
    if (priceMin) {
      searchFilters.push({ field: 'price', operator: '>=', value: priceMin });
    }
    if (settlements?.length) {
      searchFilters.push({ field: 'settlements', operator: 'in', value: settlements });
    }
    if (state) {
      searchFilters.push({ field: 'state', operator: '=', value: state });
    }
    if (types?.length) {
      searchFilters.push({ field: 'types', operator: 'in', value: types });
    }
    const filters = searchFilters.length ? searchFilters : null;
    const order = sortBy?.field !== 'default' ? sortBy : null;
    return { filters, order };
  }
}
